import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { 
  PenTool, MessageSquare, Users, Send, CalendarCheck, 
  Plus, LogOut, History, Trophy, Target, TrendingUp,
  Flame, Star, Rocket, Trash2, Calendar, User, Clock, FileText, Info, X, CheckCircle, Shield, Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_saleshustler/artifacts/lbev3bte_Gemini_Generated_Image_r450fmr450fmr450.png";

// Confetti celebration function
const celebrateBadge = () => {
  // First burst
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#4DD4E8', '#7ED957', '#FACC15', '#F472B6', '#A78BFA']
  });
  
  // Second burst after delay
  setTimeout(() => {
    confetti({
      particleCount: 50,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: ['#4DD4E8', '#7ED957', '#FACC15']
    });
  }, 150);
  
  setTimeout(() => {
    confetti({
      particleCount: 50,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: ['#4DD4E8', '#7ED957', '#FACC15']
    });
  }, 300);
};

// Category guidelines content
const CATEGORY_GUIDELINES = {
  posts: {
    title: "POSTS — 20 per week",
    purpose: "Visibility + authority",
    rule: "Original or value-added only",
    qualifies: [
      "Educational insight",
      "Perspective shift / reframing",
      "Personal lesson tied to business",
      "Framework or breakdown",
      '"What I\'m seeing lately" insight',
      "Question post (thoughtful, not bait)",
      "Short-form video",
      "Event-related post (before or after)",
      "Case example (problem → approach → outcome)",
      "Myth or misconception",
      "Values / standards post",
      "Process or behind-the-scenes",
      "Client or community win (no hype)",
      "Weekly reflection",
      "Post inspired by a conversation",
      "Resource share with your takeaway",
      "Old comment expanded into a post",
      "Comparison post (old way vs better way)",
      'Clarifying post ("people keep asking me…")',
      'Authority reminder ("here\'s what I actually do")'
    ],
    disqualifies: "Likes, reshares without commentary, or filler do NOT count."
  },
  comments: {
    title: "COMMENTS — 20 per week",
    purpose: "Recognition + familiarity",
    rule: "Must add value",
    qualifies: [
      "Adds insight or context",
      "Asks a thoughtful follow-up question",
      "Expands on the original idea",
      "Offers a respectful alternative view",
      "Clarifies confusion in the thread",
      "Shares a relevant example",
      "Provides encouragement with substance",
      "Credits or tags someone appropriately",
      "Answers a question clearly",
      "Connects two ideas meaningfully",
      "References personal experience",
      "Adds a missing perspective",
      "Follows up on a previous thread",
      "Supports a win with specificity",
      "Helps someone think differently",
      "Continues a multi-comment discussion",
      "Summarizes the thread helpfully",
      "Adds a practical takeaway",
      "Links to a helpful resource (sparingly)",
      "Closes a loop publicly"
    ],
    disqualifies: '"Great post" comments do NOT count.'
  },
  connections: {
    title: "CONNECTIONS — 20 per week",
    purpose: "Network expansion with intention",
    rule: "Context always required",
    qualifies: [
      "Sent with a personalized note",
      "References shared group, post, or event",
      "Explains why you're connecting",
      "Follows engagement or comments",
      "Reconnects with a dormant contact",
      "Introduced via a mutual connection",
      "Event-based connection",
      "Community-based connection",
      "Referral-based connection",
      "Industry-aligned connection",
      "Relationship-first connection",
      "Post-inspired connection",
      "Conversation-continuation connection",
      "Follow-up connection",
      "Intentional prospect connection",
      "Peer or collaborator connection",
      "Mentor-style connection",
      "Local-market connection",
      "Strategic relationship connection",
      "High-alignment connection"
    ],
    disqualifies: "Blind or note-less requests do NOT count."
  },
  messages: {
    title: "MESSAGES — 20 per week",
    purpose: "Trust + dialogue",
    rule: "Human, relevant, no pitch",
    qualifies: [
      "Follow-up from a comment",
      "Thank-you message",
      "Check-in message",
      "Curiosity-based question",
      "Voice note",
      "Resource share (earned)",
      "Event follow-up",
      "Reconnection message",
      '"Saw this and thought of you"',
      "Recap of a prior conversation",
      "Progress check",
      "Permission-based insight",
      "Supportive encouragement",
      "Clarifying question",
      "Scheduling-related message",
      "Context-setting introduction",
      "Value-first email",
      "Answer to an inbound message",
      "Relationship-nurture message",
      "Next-step confirmation"
    ],
    disqualifies: "Copy/paste or scripted messages do NOT count."
  },
  meetings: {
    title: "BOOKED MEETINGS — The Outcome",
    purpose: "Opportunity + momentum",
    rule: "Must be scheduled on the calendar",
    qualifies: [
      "Discovery or clarity call",
      "Strategy or advisory conversation",
      "Follow-up meeting",
      "Diagnostic session",
      "Relationship deepener",
      "Collaboration discussion",
      "Partnership conversation",
      "Client review",
      "Community-related meeting",
      "Referral-based meeting",
      "Prospect call",
      "Warm introduction meeting",
      "Group-to-1:1 conversion meeting",
      "Re-engagement meeting",
      "Event follow-up meeting",
      "Alignment conversation",
      "Decision-support meeting",
      "Planning session",
      "Assessment review",
      "Next-step execution call"
    ],
    disqualifies: '"Invites sent" do NOT count. Only confirmed on-calendar meetings count.'
  }
};

const CATEGORIES = [
  { id: "posts", label: "Posts", icon: PenTool, target: 20, color: "cyan" },
  { id: "comments", label: "Comments", icon: MessageSquare, target: 20, color: "cyan" },
  { id: "connections", label: "Connections", icon: Users, target: 20, color: "cyan" },
  { id: "messages", label: "Messages", icon: Send, target: 20, color: "cyan" },
  { id: "meetings", label: "Meetings", icon: CalendarCheck, target: null, color: "green" },
];

const BADGE_ICONS = {
  rocket: Rocket,
  trophy: Trophy,
  calendar: CalendarCheck,
  flame: Flame,
  star: Star,
  pen: PenTool,
  message: MessageSquare,
  users: Users,
  send: Send,
};

export default function DashboardPage({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [philosophyOpen, setPhilosophyOpen] = useState(false);
  const [categoryInfoOpen, setCategoryInfoOpen] = useState(null); // stores category id or null
  const [selectedCategory, setSelectedCategory] = useState("");
  const [activityCount, setActivityCount] = useState(1);
  const [activityNote, setActivityNote] = useState("");
  const [personCompany, setPersonCompany] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterWeek, setFilterWeek] = useState("current");
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const response = await fetch(`${API}/admin/check`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setIsAdmin(data.is_admin);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };
    checkAdmin();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, badgesRes, activitiesRes] = await Promise.all([
        fetch(`${API}/activities/weekly-stats`, { credentials: 'include' }),
        fetch(`${API}/badges`, { credentials: 'include' }),
        fetch(`${API}/activities`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (badgesRes.ok) {
        const badgesData = await badgesRes.json();
        setBadges(badgesData);
      }

      if (activitiesRes.ok) {
        const activitiesData = await activitiesRes.json();
        setActivities(activitiesData);
        
        // Extract unique weeks from activities
        const weeks = [...new Set(activitiesData.map(a => a.week_start))].sort().reverse();
        setAvailableWeeks(weeks);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter activities based on category and week
  const filteredActivities = activities.filter(activity => {
    const categoryMatch = filterCategory === "all" || activity.category === filterCategory;
    const weekMatch = filterWeek === "all" || activity.week_start === filterWeek || 
      (filterWeek === "current" && activity.week_start === stats?.week_start);
    return categoryMatch && weekMatch;
  });

  const handleDownloadPdf = async () => {
    try {
      const response = await fetch(`${API}/reports/user-pdf`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `integritygo_report_${user?.name?.replace(/\s+/g, '_').toLowerCase() || 'user'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download report');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login', { replace: true });
    }
  };

  const handleLogActivity = async (e) => {
    e.preventDefault();
    if (!selectedCategory) {
      toast.error("Please select a category");
      return;
    }
    
    // Require person/company and meeting date for meetings
    if (selectedCategory === 'meetings' && !personCompany) {
      toast.error("Please enter who the meeting is with");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/activities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          category: selectedCategory,
          count: activityCount,
          note: activityNote || null,
          person_company: personCompany || null,
          meeting_date: selectedCategory === 'meetings' ? meetingDate || null : null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to log activity');
      }

      const data = await response.json();
      
      toast.success(`Logged ${activityCount} ${selectedCategory}!`);
      
      // Check for new badges earned
      if (data.new_badges && data.new_badges.length > 0) {
        // Trigger confetti celebration!
        celebrateBadge();
        
        data.new_badges.forEach(badgeId => {
          const badgeNames = {
            'first_log': '🚀 First Step',
            'posts_pro': '✏️ Post Pro',
            'comments_champ': '💬 Comment Champ',
            'connections_king': '👥 Connection King',
            'messages_maven': '📨 Message Maven',
            'meeting_master': '📅 Meeting Master',
            'streak_3': '🔥 On Fire',
            'week_complete': '🏆 Weekly Warrior',
            'perfect_week': '⭐ Perfect Week'
          };
          setTimeout(() => {
            toast.success(`🎉 Achievement Unlocked: ${badgeNames[badgeId] || badgeId}!`, {
              duration: 5000,
            });
          }, 500);
        });
      }
      
      setDialogOpen(false);
      setSelectedCategory("");
      setActivityCount(1);
      setActivityNote("");
      setMeetingDate("");
      setPersonCompany("");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const quickLog = async (category) => {
    try {
      const response = await fetch(`${API}/activities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          category: category,
          count: 1,
          note: null,
          person_company: null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to log activity');
      }

      const data = await response.json();
      
      toast.success(`+1 ${category}!`);
      
      // Check for new badges earned
      if (data.new_badges && data.new_badges.length > 0) {
        // Trigger confetti celebration!
        celebrateBadge();
        
        data.new_badges.forEach(badgeId => {
          const badgeNames = {
            'first_log': '🚀 First Step',
            'posts_pro': '✏️ Post Pro',
            'comments_champ': '💬 Comment Champ',
            'connections_king': '👥 Connection King',
            'messages_maven': '📨 Message Maven',
            'meeting_master': '📅 Meeting Master',
            'streak_3': '🔥 On Fire',
            'week_complete': '🏆 Weekly Warrior',
            'perfect_week': '⭐ Perfect Week'
          };
          setTimeout(() => {
            toast.success(`🎉 Achievement Unlocked: ${badgeNames[badgeId] || badgeId}!`, {
              duration: 5000,
            });
          }, 500);
        });
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getCategoryValue = (categoryId) => {
    if (!stats) return 0;
    return stats[categoryId] || 0;
  };

  const getProgressPercentage = (value, target) => {
    if (!target) return 0;
    return Math.min((value / target) * 100, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-glow p-8 rounded-full">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const inputCategories = CATEGORIES.filter(c => c.id !== "meetings");
  const meetingCategory = CATEGORIES.find(c => c.id === "meetings");
  const totalInputs = stats?.total_inputs || 0;
  const meetings = stats?.meetings || 0;
  const ratio = meetings > 0 ? (totalInputs / meetings).toFixed(1) : "—";

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Header */}
      <header className="border-b border-white/10 bg-card/50 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="IntegrityGO" className="h-8 w-auto" />
              <span className="font-semibold text-lg hidden sm:block">Sales Engine</span>
            </div>
            
            <div className="flex items-center gap-4">
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                  data-testid="admin-btn"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Admin
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownloadPdf}
                className="text-muted-foreground hover:text-foreground"
                data-testid="download-report-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Report</span>
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/history')}
                className="text-muted-foreground hover:text-foreground"
                data-testid="history-btn"
              >
                <History className="h-4 w-4 mr-2" />
                History
              </Button>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground hidden sm:block">
                  {user?.name || user?.email}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Week Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Weekly Scoreboard</h1>
            <p className="text-muted-foreground mt-1">
              Week of {stats?.week_start || "—"} (Mon-Sun EST)
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary hover:bg-primary/90 glow-cyan"
                data-testid="log-activity-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Log Activity
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-panel border-white/10">
              <DialogHeader>
                <DialogTitle>Log Activity</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleLogActivity} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <div className="flex items-center gap-2">
                            <cat.icon className="h-4 w-4" />
                            {cat.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedCategory !== 'meetings' && (
                  <div className="space-y-2">
                    <Label>Count</Label>
                    <Input
                      type="number"
                      min={1}
                      value={activityCount}
                      onChange={(e) => setActivityCount(parseInt(e.target.value) || 1)}
                      className="bg-background/50"
                      data-testid="count-input"
                    />
                  </div>
                )}

                {/* Meeting-specific fields */}
                {selectedCategory === 'meetings' && (
                  <>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <User className="h-4 w-4 text-secondary" />
                        Who is the meeting with? *
                      </Label>
                      <Input
                        value={personCompany}
                        onChange={(e) => setPersonCompany(e.target.value)}
                        placeholder="e.g., John Smith, Acme Corp"
                        className="bg-background/50"
                        required
                        data-testid="meeting-person-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-secondary" />
                        Meeting Date
                      </Label>
                      <Input
                        type="date"
                        value={meetingDate}
                        onChange={(e) => setMeetingDate(e.target.value)}
                        className="bg-background/50"
                        data-testid="meeting-date-input"
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Note (optional)</Label>
                  <Textarea
                    value={activityNote}
                    onChange={(e) => setActivityNote(e.target.value)}
                    placeholder={selectedCategory === 'meetings' ? "Meeting agenda or notes..." : "Add a note about this activity..."}
                    className="bg-background/50"
                    data-testid="note-input"
                  />
                </div>

                {selectedCategory !== 'meetings' && (
                  <div className="space-y-2">
                    <Label>Person/Company (optional)</Label>
                    <Input
                      value={personCompany}
                      onChange={(e) => setPersonCompany(e.target.value)}
                      placeholder="Who was this with?"
                      className="bg-background/50"
                      data-testid="person-input"
                    />
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={submitting}
                  data-testid="submit-activity-btn"
                >
                  {submitting ? "Logging..." : "Log Activity"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Philosophy Banner - Clickable */}
        <Dialog open={philosophyOpen} onOpenChange={setPhilosophyOpen}>
          <DialogTrigger asChild>
            <Card 
              className="glass-panel border-white/10 bg-gradient-to-r from-[#4DD4E8]/10 to-[#7ED957]/10 cursor-pointer hover:border-primary/50 transition-all group"
              data-testid="philosophy-banner"
            >
              <CardContent className="py-6">
                <div className="flex items-center gap-4">
                  <Target className="h-8 w-8 text-primary flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <div className="flex-1">
                    <p className="text-lg font-medium text-foreground flex items-center gap-2">
                      The 20x5 Philosophy
                      <span className="text-xs text-primary bg-primary/20 px-2 py-0.5 rounded-full">Click to learn more</span>
                    </p>
                    <p className="text-muted-foreground">
                      <span className="text-primary">Inputs</span> create the conditions. 
                      <span className="text-secondary ml-1">Meetings</span> are the result.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </DialogTrigger>
          <DialogContent className="glass-panel border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-gradient-cyan">The 20x5 Framework</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4 text-foreground">
              {/* Intro */}
              <div className="space-y-3">
                <h2 className="text-xl font-semibold text-primary">The 20x5 Philosophy</h2>
                <p className="text-muted-foreground italic">How Consistent Action Creates Booked Meetings — Without Chasing or Pitching</p>
              </div>

              {/* What 20x5 Is */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">What 20x5 Is</h3>
                <p className="text-muted-foreground">20x5 is a weekly execution framework designed to create booked meetings as a natural outcome, not a forced goal.</p>
                <p className="text-muted-foreground">Instead of chasing appointments, the system focuses on four repeatable activities that consistently lead to meaningful conversations and, eventually, meetings with the right people.</p>
                <div className="bg-white/5 p-4 rounded-xl border border-white/10 space-y-2">
                  <p className="font-medium">The formula is simple:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-primary">20 Posts</span>
                    <span className="text-primary">20 Comments</span>
                    <span className="text-primary">20 Connections</span>
                    <span className="text-primary">20 Messages</span>
                  </div>
                  <p className="text-secondary font-medium">→ Booked Meetings (the result)</p>
                </div>
                <p className="text-muted-foreground">That's 80 intentional actions per week that create the conditions where meetings make sense.</p>
              </div>

              {/* Core Principle */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">The Core Principle</h3>
                <p className="text-secondary font-medium text-lg">Meetings are not a task — they are a consequence.</p>
                <p className="text-muted-foreground">If you try to "hit a meeting quota," you create pressure, awkward outreach, and resistance.</p>
                <p className="text-muted-foreground">If you build visibility, familiarity, access, and trust, meetings happen naturally.</p>
                <p className="text-primary font-medium">20x5 is designed to do exactly that.</p>
              </div>

              {/* How the System Flows */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">How the System Flows</h3>
                <p className="text-muted-foreground">Every booked meeting should be traceable back to at least two of the four drivers:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                  <li>A post someone resonated with</li>
                  <li>A comment that built recognition</li>
                  <li>A connection that made sense</li>
                  <li>A message that created clarity</li>
                </ul>
                <div className="bg-primary/10 p-4 rounded-xl border border-primary/30">
                  <p className="font-medium text-primary">The natural progression:</p>
                  <p className="text-lg mt-2">Post → Comment → Connect → Message → <span className="text-secondary">Meeting</span></p>
                </div>
                <p className="text-muted-foreground">You don't skip steps. You don't force outcomes. You let momentum do the work.</p>
              </div>

              {/* The Four Drivers */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">The Four Drivers (What You Do Every Week)</h3>
                
                {/* Posts */}
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <h4 className="font-semibold text-primary flex items-center gap-2">
                    <PenTool className="h-4 w-4" /> 1) Posts — 20 per Week
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1 mb-2"><strong>Purpose:</strong> Visibility and authority</p>
                  <p className="text-muted-foreground text-sm">Posts are how people learn what you believe, how you think, and who you're for. Each post should teach something, reframe a common problem, share insight from experience, or clarify how you approach your work.</p>
                  <p className="text-primary text-sm mt-2">Posts are not advertisements. They are signals.</p>
                </div>

                {/* Comments */}
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <h4 className="font-semibold text-primary flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" /> 2) Comments — 20 per Week
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1 mb-2"><strong>Purpose:</strong> Recognition and familiarity</p>
                  <p className="text-muted-foreground text-sm">Comments warm the room before you enter it. Instead of cold connections, people begin to recognize your name, associate you with insight, and feel comfortable engaging.</p>
                  <p className="text-primary text-sm mt-2">Comments turn visibility into familiarity — which makes everything downstream easier.</p>
                </div>

                {/* Connections */}
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <h4 className="font-semibold text-primary flex items-center gap-2">
                    <Users className="h-4 w-4" /> 3) Connections — 20 per Week
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1 mb-2"><strong>Purpose:</strong> Access with context</p>
                  <p className="text-muted-foreground text-sm">Every connection request should reference a post, comment, or shared space, clearly explain why you're connecting, and feel human and relevant.</p>
                  <p className="text-primary text-sm mt-2">Connections give you permission to continue the conversation, not permission to pitch.</p>
                </div>

                {/* Messages */}
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <h4 className="font-semibold text-primary flex items-center gap-2">
                    <Send className="h-4 w-4" /> 4) Messages — 20 per Week
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1 mb-2"><strong>Purpose:</strong> Trust and clarity</p>
                  <p className="text-muted-foreground text-sm">Messages are where real dialogue happens. They're used to follow up on engagement, ask curiosity-based questions, understand goals or challenges, and continue conversations that already started.</p>
                  <p className="text-primary text-sm mt-2">Messages are never scripted and never salesy. Build enough clarity and trust that a meeting becomes the obvious next step.</p>
                </div>
              </div>

              {/* Booked Meetings */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-secondary flex items-center gap-2">
                  <CalendarCheck className="h-5 w-5" /> Booked Meetings — The Outcome
                </h3>
                <p className="text-muted-foreground">Meetings happen when:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                  <li>the problem is clear</li>
                  <li>the conversation has momentum</li>
                  <li>trust already exists</li>
                  <li>a next step feels helpful, not forced</li>
                </ul>
                <p className="text-secondary font-medium">You don't chase meetings. You invite them when it makes sense.</p>
              </div>

              {/* Why This Works */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">Why This Works</h3>
                <p className="text-muted-foreground">Most people try to jump straight to asking for meetings. 20x5 forces the correct order:</p>
                <div className="bg-secondary/10 p-4 rounded-xl border border-secondary/30 space-y-1">
                  <p>Visibility <span className="text-muted-foreground">before</span> access</p>
                  <p>Familiarity <span className="text-muted-foreground">before</span> conversation</p>
                  <p>Trust <span className="text-muted-foreground">before</span> invitation</p>
                </div>
                <p className="text-muted-foreground">This removes pressure, awkward outreach, rejection cycles, and burnout. And replaces it with consistency, confidence, momentum, and better conversations with better people.</p>
              </div>

              {/* The Discipline Rule */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold">The Discipline Rule</h3>
                <p className="text-muted-foreground">The system only works if all four drivers are done every week.</p>
                <p className="text-muted-foreground">If meetings slow down, you don't push harder — you look upstream:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                  <li>Are posts clear and consistent?</li>
                  <li>Are comments meaningful?</li>
                  <li>Are connections contextual?</li>
                  <li>Are messages human and relevant?</li>
                </ul>
                <p className="text-primary font-medium">Fix the inputs, and the outcome corrects itself.</p>
              </div>

              {/* The Standard */}
              <div className="bg-gradient-to-r from-[#4DD4E8]/10 to-[#7ED957]/10 p-6 rounded-xl border border-white/10 space-y-3">
                <h3 className="text-lg font-semibold">The Standard</h3>
                <p className="text-muted-foreground">20x5 is not about doing more. It's about doing the right things, consistently, without shortcuts.</p>
                <p className="text-muted-foreground">You don't earn meetings by asking for them. You earn them by showing up with clarity, value, and integrity — week after week.</p>
                <p className="text-xl font-semibold text-gradient-cyan mt-4">That's the system. That's why it works.</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Categories */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Weekly Inputs (Target: 20 each)
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {inputCategories.map((category) => {
                const value = getCategoryValue(category.id);
                const percentage = getProgressPercentage(value, category.target);
                const isComplete = percentage >= 100;
                
                return (
                  <Card 
                    key={category.id} 
                    className={`glass-panel border-white/10 activity-card ${isComplete ? 'glow-cyan' : ''}`}
                    data-testid={`${category.id}-card`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${isComplete ? 'bg-primary/20' : 'bg-white/5'}`}>
                            <category.icon className={`h-5 w-5 ${isComplete ? 'text-primary' : 'text-muted-foreground'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-foreground">{category.label}</h3>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCategoryInfoOpen(category.id);
                                }}
                                className="text-primary/60 hover:text-primary transition-colors"
                                data-testid={`info-${category.id}`}
                              >
                                <Info className="h-4 w-4" />
                              </button>
                            </div>
                            <p className="text-xs text-muted-foreground">Target: {category.target}/week</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-10 w-10 rounded-full bg-primary/10 hover:bg-primary/20 text-primary ripple-effect"
                          onClick={() => quickLog(category.id)}
                          data-testid={`quick-log-${category.id}`}
                        >
                          <Plus className="h-5 w-5" />
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-end justify-between">
                          <span className="font-mono-display text-3xl font-medium text-foreground">
                            {value}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            / {category.target}
                          </span>
                        </div>
                        <Progress 
                          value={percentage} 
                          className="h-3 bg-white/5"
                          indicatorClassName="progress-bar-cyan progress-animate"
                        />
                        <p className="text-xs text-muted-foreground text-right">
                          {Math.round(percentage)}% complete
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Outcome Section */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-secondary" />
              Weekly Outcome
            </h2>
            
            {/* Meetings Card */}
            <Card 
              className="glass-panel border-white/10 glow-green"
              data-testid="meetings-card"
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-secondary/20">
                      <CalendarCheck className="h-5 w-5 text-secondary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-foreground">Booked Meetings</h3>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCategoryInfoOpen('meetings');
                          }}
                          className="text-secondary/60 hover:text-secondary transition-colors"
                          data-testid="info-meetings"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">The result of your inputs</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 rounded-full bg-secondary/10 hover:bg-secondary/20 text-secondary ripple-effect"
                    onClick={() => quickLog('meetings')}
                    data-testid="quick-log-meetings"
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </div>
                
                <div className="text-center py-4">
                  <span className="font-mono-display text-5xl font-medium text-secondary">
                    {meetings}
                  </span>
                  <p className="text-sm text-muted-foreground mt-2">meetings this week</p>
                </div>
              </CardContent>
            </Card>

            {/* Ratio Card */}
            <Card className="glass-panel border-white/10" data-testid="ratio-card">
              <CardContent className="pt-6">
                <h3 className="font-medium text-foreground mb-4">Inputs per Meeting</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-mono-display text-2xl text-foreground">{totalInputs}</span>
                    <span className="text-muted-foreground ml-2">inputs</span>
                  </div>
                  <div className="text-2xl text-muted-foreground">/</div>
                  <div>
                    <span className="font-mono-display text-2xl text-secondary">{meetings}</span>
                    <span className="text-muted-foreground ml-2">meetings</span>
                  </div>
                  <div className="text-2xl text-muted-foreground">=</div>
                  <div>
                    <span className="font-mono-display text-2xl text-primary">{ratio}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  {meetings > 0 
                    ? `You're converting about ${ratio} inputs per meeting.`
                    : "Keep adding inputs to book meetings!"}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Badges Section */}
        <Card className="glass-panel border-white/10" data-testid="badges-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-[#FACC15]" />
              Achievements
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({badges.filter(b => b.earned_at).length}/{badges.length} unlocked)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {badges.map((badge) => {
                const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
                const isEarned = !!badge.earned_at;
                
                // Different colors for different badge types
                const getBadgeColor = (badgeId) => {
                  switch(badgeId) {
                    case 'first_log': return { bg: 'bg-emerald-500/20', border: 'border-emerald-500/50', text: 'text-emerald-400', glow: 'shadow-[0_0_15px_rgba(52,211,153,0.4)]', progressBg: 'bg-emerald-500' };
                    case 'posts_pro': return { bg: 'bg-[#4DD4E8]/20', border: 'border-[#4DD4E8]/50', text: 'text-[#4DD4E8]', glow: 'shadow-[0_0_15px_rgba(77,212,232,0.4)]', progressBg: 'bg-[#4DD4E8]' };
                    case 'comments_champ': return { bg: 'bg-sky-500/20', border: 'border-sky-500/50', text: 'text-sky-400', glow: 'shadow-[0_0_15px_rgba(56,189,248,0.4)]', progressBg: 'bg-sky-500' };
                    case 'connections_king': return { bg: 'bg-indigo-500/20', border: 'border-indigo-500/50', text: 'text-indigo-400', glow: 'shadow-[0_0_15px_rgba(129,140,248,0.4)]', progressBg: 'bg-indigo-500' };
                    case 'messages_maven': return { bg: 'bg-violet-500/20', border: 'border-violet-500/50', text: 'text-violet-400', glow: 'shadow-[0_0_15px_rgba(167,139,250,0.4)]', progressBg: 'bg-violet-500' };
                    case 'meeting_master': return { bg: 'bg-[#7ED957]/20', border: 'border-[#7ED957]/50', text: 'text-[#7ED957]', glow: 'shadow-[0_0_15px_rgba(126,217,87,0.4)]', progressBg: 'bg-[#7ED957]' };
                    case 'streak_3': return { bg: 'bg-orange-500/20', border: 'border-orange-500/50', text: 'text-orange-400', glow: 'shadow-[0_0_15px_rgba(249,115,22,0.4)]', progressBg: 'bg-orange-500' };
                    case 'week_complete': return { bg: 'bg-[#FACC15]/20', border: 'border-[#FACC15]/50', text: 'text-[#FACC15]', glow: 'shadow-[0_0_15px_rgba(250,204,21,0.4)]', progressBg: 'bg-[#FACC15]' };
                    case 'perfect_week': return { bg: 'bg-purple-500/20', border: 'border-purple-500/50', text: 'text-purple-400', glow: 'shadow-[0_0_15px_rgba(168,85,247,0.4)]', progressBg: 'bg-purple-500' };
                    default: return { bg: 'bg-[#FACC15]/20', border: 'border-[#FACC15]/50', text: 'text-[#FACC15]', glow: 'shadow-[0_0_15px_rgba(250,204,21,0.4)]', progressBg: 'bg-[#FACC15]' };
                  }
                };
                
                // Calculate progress for each badge
                const getBadgeProgress = (badgeId) => {
                  if (!stats) return { current: 0, target: 1, percent: 0 };
                  switch(badgeId) {
                    case 'first_log': 
                      return { current: activities.length > 0 ? 1 : 0, target: 1, percent: activities.length > 0 ? 100 : 0 };
                    case 'posts_pro':
                      return { current: stats?.posts || 0, target: 20, percent: Math.min(((stats?.posts || 0) / 20) * 100, 100) };
                    case 'comments_champ':
                      return { current: stats?.comments || 0, target: 20, percent: Math.min(((stats?.comments || 0) / 20) * 100, 100) };
                    case 'connections_king':
                      return { current: stats?.connections || 0, target: 20, percent: Math.min(((stats?.connections || 0) / 20) * 100, 100) };
                    case 'messages_maven':
                      return { current: stats?.messages || 0, target: 20, percent: Math.min(((stats?.messages || 0) / 20) * 100, 100) };
                    case 'meeting_master': 
                      return { current: meetings, target: 5, percent: Math.min((meetings / 5) * 100, 100) };
                    case 'streak_3': 
                      return { current: isEarned ? 3 : 0, target: 3, percent: isEarned ? 100 : 0 };
                    case 'week_complete': 
                      return { current: totalInputs, target: 80, percent: Math.min((totalInputs / 80) * 100, 100) };
                    case 'perfect_week': {
                      const posts = stats?.posts || 0;
                      const comments = stats?.comments || 0;
                      const connections = stats?.connections || 0;
                      const messages = stats?.messages || 0;
                      const completed = [posts >= 20, comments >= 20, connections >= 20, messages >= 20].filter(Boolean).length;
                      return { current: completed, target: 4, percent: (completed / 4) * 100 };
                    }
                    default: 
                      return { current: 0, target: 1, percent: 0 };
                  }
                };
                
                const colors = getBadgeColor(badge.badge_id);
                const progress = getBadgeProgress(badge.badge_id);
                
                // Determine icon color based on progress
                const getIconColor = () => {
                  if (progress.percent >= 100) return 'text-emerald-400'; // Green at 100%
                  if (progress.percent >= 50) return 'text-yellow-400';   // Yellow at 50%+
                  return 'text-muted-foreground';                          // Gray below 50%
                };
                
                const getIconBgColor = () => {
                  if (progress.percent >= 100) return 'bg-emerald-500/20';
                  if (progress.percent >= 50) return 'bg-yellow-500/20';
                  return 'bg-white/10';
                };
                
                return (
                  <div
                    key={badge.badge_id}
                    className={`flex flex-col p-4 rounded-xl border transition-all duration-300 ${
                      isEarned 
                        ? `${colors.border} ${colors.bg} ${colors.glow}` 
                        : 'border-white/10 bg-white/5'
                    }`}
                    data-testid={`badge-${badge.badge_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-xl transition-all duration-300 ${getIconBgColor()}`}>
                        <IconComponent className={`h-6 w-6 transition-all duration-300 ${getIconColor()}`} />
                      </div>
                      <div className="flex-1">
                        <p className={`font-semibold ${isEarned ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {badge.name}
                        </p>
                        <p className="text-xs text-muted-foreground">{badge.description}</p>
                      </div>
                      {isEarned && (
                        <Badge className={`${colors.bg} ${colors.text} border-0`}>
                          ✓
                        </Badge>
                      )}
                    </div>
                    
                    {/* Progress bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className={isEarned ? colors.text : 'text-muted-foreground'}>
                          {isEarned ? 'Completed!' : `${progress.current}/${progress.target}`}
                        </span>
                        <span className={isEarned ? colors.text : 'text-muted-foreground'}>
                          {Math.round(progress.percent)}%
                        </span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${isEarned ? colors.progressBg : 'bg-muted-foreground/30'}`}
                          style={{ width: `${progress.percent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Activity Log Section */}
        <Card className="glass-panel border-white/10" data-testid="activity-log">
          <CardHeader>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Activity Log
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActivityLogOpen(!activityLogOpen)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {activityLogOpen ? 'Show Less' : `View All (${filteredActivities.length})`}
                </Button>
              </div>
              
              {/* Filter Controls */}
              <div className="flex flex-wrap gap-3">
                {/* Category Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Category:</span>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-[140px] h-8 text-xs bg-background/50" data-testid="filter-category">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <div className="flex items-center gap-2">
                            <cat.icon className="h-3 w-3" />
                            {cat.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Week Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Week:</span>
                  <Select value={filterWeek} onValueChange={setFilterWeek}>
                    <SelectTrigger className="w-[160px] h-8 text-xs bg-background/50" data-testid="filter-week">
                      <SelectValue placeholder="Current Week" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="current">Current Week</SelectItem>
                      <SelectItem value="all">All Weeks</SelectItem>
                      {availableWeeks.map((week) => (
                        <SelectItem key={week} value={week}>
                          Week of {new Date(week + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Clear Filters */}
                {(filterCategory !== "all" || filterWeek !== "current") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setFilterCategory("all");
                      setFilterWeek("current");
                    }}
                    data-testid="clear-filters"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredActivities.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {activities.length === 0 
                  ? "No activities logged yet. Start tracking your 20x5 inputs!"
                  : "No activities match your filters."}
              </p>
            ) : (
              <div className="space-y-3">
                {(activityLogOpen ? filteredActivities : filteredActivities.slice(0, 5)).map((activity) => {
                  const category = CATEGORIES.find(c => c.id === activity.category);
                  const IconComponent = category?.icon || PenTool;
                  const isMeeting = activity.category === 'meetings';
                  
                  return (
                    <div
                      key={activity.activity_id}
                      className={`p-4 rounded-xl border transition-all ${
                        isMeeting 
                          ? 'border-secondary/30 bg-secondary/5' 
                          : 'border-white/10 bg-white/5'
                      }`}
                      data-testid={`activity-${activity.activity_id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-2.5 rounded-xl ${isMeeting ? 'bg-secondary/20' : 'bg-primary/20'}`}>
                          <IconComponent className={`h-5 w-5 ${isMeeting ? 'text-secondary' : 'text-primary'}`} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-semibold ${isMeeting ? 'text-secondary' : 'text-primary'}`}>
                              {isMeeting ? 'Booked Meeting' : `+${activity.count} ${category?.label || activity.category}`}
                            </span>
                            {isMeeting && (
                              <Badge className="bg-secondary/20 text-secondary border-0 text-xs">
                                Outcome
                              </Badge>
                            )}
                          </div>
                          
                          {/* Meeting-specific details */}
                          {isMeeting && activity.person_company && (
                            <div className="flex items-center gap-2 text-sm text-foreground mb-1">
                              <User className="h-3.5 w-3.5 text-muted-foreground" />
                              <span>{activity.person_company}</span>
                            </div>
                          )}
                          
                          {isMeeting && activity.meeting_date && (
                            <div className="flex items-center gap-2 text-sm text-foreground mb-1">
                              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                              <span>Meeting on {new Date(activity.meeting_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                            </div>
                          )}
                          
                          {/* Non-meeting person/company */}
                          {!isMeeting && activity.person_company && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                              <User className="h-3.5 w-3.5" />
                              <span>{activity.person_company}</span>
                            </div>
                          )}
                          
                          {/* Note */}
                          {activity.note && (
                            <p className="text-sm text-muted-foreground mt-1">
                              "{activity.note}"
                            </p>
                          )}
                        </div>
                        
                        {/* Timestamp and Delete */}
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span>
                              {new Date(activity.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-red-400 hover:bg-red-400/10"
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API}/activities/${activity.activity_id}`, {
                                  method: 'DELETE',
                                  credentials: 'include'
                                });
                                if (res.ok) {
                                  toast.success('Activity deleted');
                                  fetchData();
                                }
                              } catch (err) {
                                toast.error('Failed to delete');
                              }
                            }}
                            data-testid={`delete-${activity.activity_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Category Info Dialog */}
      <Dialog open={categoryInfoOpen !== null} onOpenChange={(open) => !open && setCategoryInfoOpen(null)}>
        <DialogContent className="glass-panel border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          {categoryInfoOpen && CATEGORY_GUIDELINES[categoryInfoOpen] && (
            <>
              <DialogHeader>
                <DialogTitle className={`text-xl font-bold flex items-center gap-2 ${categoryInfoOpen === 'meetings' ? 'text-secondary' : 'text-primary'}`}>
                  {CATEGORIES.find(c => c.id === categoryInfoOpen)?.icon && 
                    (() => {
                      const IconComp = CATEGORIES.find(c => c.id === categoryInfoOpen)?.icon;
                      return <IconComp className="h-5 w-5" />;
                    })()
                  }
                  {CATEGORY_GUIDELINES[categoryInfoOpen].title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Purpose and Rule */}
                <div className="flex gap-4">
                  <div className="flex-1 bg-white/5 p-3 rounded-lg border border-white/10">
                    <p className="text-xs text-muted-foreground mb-1">Purpose</p>
                    <p className={`font-medium ${categoryInfoOpen === 'meetings' ? 'text-secondary' : 'text-primary'}`}>
                      {CATEGORY_GUIDELINES[categoryInfoOpen].purpose}
                    </p>
                  </div>
                  <div className="flex-1 bg-white/5 p-3 rounded-lg border border-white/10">
                    <p className="text-xs text-muted-foreground mb-1">Rule</p>
                    <p className="font-medium text-foreground">
                      {CATEGORY_GUIDELINES[categoryInfoOpen].rule}
                    </p>
                  </div>
                </div>

                {/* What Qualifies */}
                <div>
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                    What qualifies as a {CATEGORIES.find(c => c.id === categoryInfoOpen)?.label}:
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {CATEGORY_GUIDELINES[categoryInfoOpen].qualifies.map((item, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-2 text-sm text-muted-foreground p-2 rounded-lg bg-white/5"
                      >
                        <span className={`mt-1 ${categoryInfoOpen === 'meetings' ? 'text-secondary' : 'text-primary'}`}>•</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What Doesn't Qualify */}
                <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl">
                  <h3 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                    <X className="h-4 w-4" />
                    Does NOT count:
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {CATEGORY_GUIDELINES[categoryInfoOpen].disqualifies}
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
